// src/components/Admin/Campaigns/builder/ai/AiFieldNote.tsx
//
// An on-demand, single-field AI suggestion chip. Nothing runs until the admin
// presses the trigger (useFieldNote is armed only by generate()). When a note
// arrives it is shown as an accent left-border chip with a "Use this" action
// that hands the value back to the parent — this component never writes anything
// itself. A null reply is handled gracefully with a subtle "no suggestion".

import React from 'react';
import { GhostButton, PrimaryButton } from '../primitives';
import { useFieldNote } from '../../ai/useCampaignAi';
import styles from './guide.module.css';

export function AiFieldNote(props: {
  entity: 'campaign' | 'step';
  field: string;
  context: Record<string, unknown>;
  onUse: (value: string) => void;
}): JSX.Element {
  const { entity, field, context, onUse } = props;

  // Tracks whether the admin has asked for a suggestion in this open cycle;
  // used to distinguish "not requested yet" from "requested, came back empty".
  const [requested, setRequested] = React.useState(false);

  const { data, isLoading, generate } = useFieldNote({
    entity,
    field,
    context,
    enabled: true,
  });

  const handleGenerate = React.useCallback(() => {
    setRequested(true);
    generate();
  }, [generate]);

  const dismiss = React.useCallback(() => {
    setRequested(false);
  }, []);

  if (!requested) {
    return (
      <GhostButton className={styles.noteTrigger} onClick={handleGenerate}>
        ✨ AI suggestion
      </GhostButton>
    );
  }

  return (
    <div className={styles.noteWrap}>
      <div className={styles.aiNote}>
        <div className={styles.aiNoteBody}>
          {isLoading ? (
            <p className={styles.aiNoteMuted}>Generating a suggestion…</p>
          ) : data ? (
            <>
              <p className={styles.aiNoteText}>{data}</p>
              <div className={styles.aiNoteActions}>
                <PrimaryButton
                  className={styles.proposalBtn}
                  onClick={() => onUse(data)}
                >
                  Use this
                </PrimaryButton>
                <GhostButton className={styles.noteTrigger} onClick={handleGenerate}>
                  Regenerate
                </GhostButton>
              </div>
            </>
          ) : (
            <p className={styles.aiNoteMuted}>
              No suggestion — you can write this one yourself.
            </p>
          )}
        </div>
        <button
          type="button"
          className={styles.noteDismiss}
          onClick={dismiss}
          aria-label="Dismiss suggestion"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export default AiFieldNote;
